/* Atlas V3 Desktop — router / root.
   Opt-in desktop web experience gated by the /v3 path in App.jsx. Wraps the
   tree in the desktop TickerPanelProvider (right-dock detail + ?stock= deep
   links + TickerCtx). Internal page nav is local state — the path stays /v3 so
   the gate holds while navigating. V2 desktop + mobile are untouched. */
import { useEffect, useMemo, useState } from 'react'
import './v3web.css'
import { useTicker } from '@/v2/v3/lib'
import { useTickerPanel } from './TickerPanelProvider'
import { NewsSheet } from '@/v2/v3/NewsSheet'
import { PickSheet } from '@/v2/v3/PickSheet'
import { useAtlasStocks } from '@/v2/hooks/useAtlasStocks'
import { useAdrUniverse } from '@/v2/hooks/useAdrUniverse'
import { useLeveragedEtfs } from '@/v2/hooks/useLeveragedEtfs'
import { useWatchlists } from '@/v2/hooks/useWatchlists'
import { useSignalsToday, usePickOfWeek } from '@/v2/hooks/useSupabaseData'
import { UniversePriceProvider } from '@/v2/hooks/useUniversePrices'
import { TickerPanelProvider } from './TickerPanelProvider'
import { DesktopShell } from './DesktopShell'
import { Overview } from './pages/Overview'
import { Signals } from './pages/Signals'
import { Markets } from './pages/Markets'
import { MyStocks } from './pages/MyStocks'
import { Insights } from './pages/Insights'
import { Cycle } from './pages/Cycle'
import { Calendar } from './pages/Calendar'
import { Settings } from './pages/Settings'
import { AgentDesk } from './pages/AgentDesk'
import { Admin } from './pages/Admin'

export function DesktopV3Router() {
  // Full universe (hunting incl.) + ADRs + 2× leveraged ETFs → extraStocks so
  // ANY ticker a user can watchlist opens in the dock instead of no-oping.
  const { stocks: allStocks, sectors } = useAtlasStocks({ includeInactive: true })
  const { us, home } = useAdrUniverse()
  const { instruments: levInstruments } = useLeveragedEtfs()
  const extraStocks = useMemo(() => ([
    ...allStocks,
    ...us.map((s) => ({ ticker: s.ticker, name: s.company, sector: s.sector })),
    ...home.map((s) => ({ ticker: s.ticker, name: s.company, sector: s.sector })),
    ...levInstruments.flatMap((inst) => (inst.etfs || []).filter((e) => e.ticker).map((e) => ({
      ticker: e.ticker, name: `${e.issuer || ''} ${e.leverage || 2}× Long ${inst.underlying}`.trim(), sector: inst.sector,
    }))),
  ]), [allStocks, us, home, levInstruments])

  const coverage = useMemo(() => ({
    tickers: allStocks.length,
    sectors: sectors?.length || new Set(allStocks.map((s) => s.sector).filter(Boolean)).size,
  }), [allStocks, sectors])

  return (
    <UniversePriceProvider>
      <TickerPanelProvider extraStocks={extraStocks}>
        <Inner coverage={coverage} />
      </TickerPanelProvider>
    </UniversePriceProvider>
  )
}

function Inner({ coverage }) {
  const openTicker = useTicker()
  const { close: closeDock } = useTickerPanel()
  // /admin deep-link lands on the admin page (page nav stays local-state after
  // that, consistent with the rest of V3 — the URL stays put). Otherwise the
  // Settings "Open to" pref (default_tab, via the useUserPref localStorage
  // mirror) picks the launch page — launch-time only, nav is unaffected.
  const [page, setPage] = useState(() => {
    if (typeof window === 'undefined') return 'overview'
    if (window.location.pathname.startsWith('/admin')) return 'admin'
    try { if (JSON.parse(window.localStorage.getItem('atlas.pref.default_tab')) === 'watch') return 'mystocks' } catch { /* default */ }
    return 'overview'
  })
  const [newsItem, setNewsItem] = useState(null)
  const [pickOpen, setPickOpen] = useState(false)

  const { events: todaySignals } = useSignalsToday()
  const { watchlists } = useWatchlists()
  const { active: pick, stats, history } = usePickOfWeek()

  const counts = useMemo(() => ({
    signals: (todaySignals || []).length || undefined,
    mystocks: new Set((watchlists || []).flatMap((w) => w.tickers || [])).size || undefined,
  }), [todaySignals, watchlists])

  // The reader sheets (mobile components, z70) would render UNDER the right
  // dock (z150) — so opening one closes the dock instead of layering over it.
  const openNews = (item) => { closeDock(); setNewsItem(item) }
  const openPick = () => { closeDock(); setPickOpen(true) }

  // Esc closes the sheets — but only when the ⌘K palette (the topmost layer)
  // isn't open, so one press dismisses one layer, not the whole stack.
  useEffect(() => {
    if (!newsItem && !pickOpen) return
    const h = (e) => {
      if (e.key !== 'Escape' || document.querySelector('.v3web-cmdk')) return
      setNewsItem(null); setPickOpen(false)
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [newsItem, pickOpen])

  const openTickerFromSheet = (t) => { setNewsItem(null); setPickOpen(false); openTicker(t) }

  const pages = {
    overview: <Overview openNews={openNews} openPick={openPick} />,
    signals: <Signals openNews={openNews} />,
    markets: <Markets />,
    mystocks: <MyStocks />,
    insights: <Insights />,
    cycle: <Cycle />,
    calendar: <Calendar />,
    agent: <AgentDesk />,
    settings: <Settings />,
    admin: <Admin />, // always mounted route — the page self-gates on useIsAdmin
  }

  return (
    <>
      <DesktopShell active={page} onNav={setPage} counts={counts} coverage={coverage}>
        {/* keyed wrapper → each page switch enters with the single editorial fade */}
        <div key={page} className="v3web-pagefade">
          {pages[page] || pages.overview}
        </div>
      </DesktopShell>

      {newsItem && (
        <div className="v3-root" data-v3web-sheet="1">
          <NewsSheet item={newsItem} onClose={() => setNewsItem(null)} onTicker={openTickerFromSheet} desktop />
        </div>
      )}
      {pickOpen && pick && (
        <div className="v3-root" data-v3web-sheet="1">
          <PickSheet active={pick} stats={stats} history={history} onClose={() => setPickOpen(false)} openTicker={openTickerFromSheet} />
        </div>
      )}
    </>
  )
}
